import { useState, useEffect } from "react";
import {
  Home, MessageSquare,
  Users, Settings, ChevronLeft, ChevronRight,
  Headphones, RefreshCw
} from "lucide-react";
import { supabase } from "../supabase";

const PLAN_PRICES = {
  nano:    { EUR: '19€',    MAD: '190 MAD', FCFA: '12 500 FCFA', NGN: '31 000 NGN', GBP: '16£',  USD: '21$' },
  starter: { EUR: '59€',   MAD: '590 MAD', FCFA: '38 500 FCFA', NGN: '97 000 NGN', GBP: '50£',  USD: '64$' },
  pro:     { EUR: '119€',  MAD: '1 190 MAD', FCFA: '78 000 FCFA', NGN: '196 000 NGN', GBP: '101£', USD: '129$' },
  premium: { EUR: 'Sur devis', MAD: 'Sur devis', FCFA: 'Sur devis', NGN: 'Sur devis', GBP: 'Sur devis', USD: 'Sur devis' },
  trial:   { EUR: 'Essai gratuit', MAD: 'Essai gratuit', FCFA: 'Essai gratuit', NGN: 'Essai gratuit', GBP: 'Essai gratuit', USD: 'Essai gratuit' },
};

export default function Sidebar({ currentPage, setCurrentPage, isOpen, setIsOpen, metrics, canRelance, businessMode }) {
  const [org, setOrg] = useState(null);

  useEffect(() => {
  async function loadOrg() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase
      .from('organizations')
      .select('name, plan_name, subscription_plan, is_trial, currency')
      .eq('user_id', user.id)
      .single();
    setOrg(data);
  }
    loadOrg();
  }, []);

  const planName = org?.is_trial ? 'trial' : (org?.plan_name || org?.subscription_plan || 'starter');
  const planLabel = planName.charAt(0).toUpperCase() + planName.slice(1);
  const currency = org?.currency || 'EUR';
  const planPrice = PLAN_PRICES[planName]?.[currency] || PLAN_PRICES[planName]?.EUR || '';
  const orgInitials = (org?.name || 'HB').slice(0, 2).toUpperCase();

  const navItems = [
    { id: "home",     label: "Tableau de bord", icon: Home },
    { id: "messages", label: "Messages",        icon: MessageSquare },
    { id: "support",  label: "Support client",  icon: Headphones },
    { id: "contacts", label: "Contacts",        icon: Users },
    ...(canRelance ? [{ id: "relances", label: "Relances", icon: RefreshCw }] : []),
    { id: "settings", label: "Paramètres",      icon: Settings },
  ];

  const badges = {
    orders:  metrics?.ordersPending || 0,
    support: metrics?.ticketsOpen   || 0,
  };

  return (
    <div className={`flex flex-col bg-orange-700 transition-all duration-300 ${isOpen ? "w-56" : "w-16"}`}>

      {/* Logo */}
      <div className="flex items-center justify-between px-4 py-5 border-b border-orange-600">
        {isOpen && (
          <div className="flex items-center gap-2">
            <div style={{width:'32px',height:'32px',background:'rgba(255,255,255,0.2)',borderRadius:'9px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
              <svg width="20" height="20" viewBox="0 0 22 22" fill="none">
                <rect x="2" y="4" width="18" height="12" rx="3.5" fill="white"/>
                <path d="M8 16 L5 20 L13 16" fill="white"/>
                <circle cx="7" cy="10" r="1.4" fill="#C04828"/>
                <circle cx="11" cy="10" r="1.4" fill="#C04828"/>
                <circle cx="15" cy="10" r="1.4" fill="#C04828"/>
              </svg>
            </div>
            <div className="text-white font-medium text-sm">
              Hellodia <span style={{opacity:0.6,fontWeight:400}}>CRM</span>
            </div>
          </div>
        )}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="text-white hover:bg-orange-600 p-1.5 rounded-lg transition-colors"
        >
          {isOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentPage === item.id;
          const badge = badges[item.id];
          return (
            <button
              key={item.id}
              onClick={() => setCurrentPage(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left
                ${isActive
                  ? "bg-white/20 text-white"
                  : "text-orange-200 hover:bg-white/10 hover:text-white"
                }`}
            >
              <Icon size={18} className="flex-shrink-0" />
              {isOpen && (
                <>
                  <span className="text-sm flex-1">{item.label}</span>
                  {badge > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-red-500 text-white">
                      {badge}
                    </span>
                  )}
                </>
              )}
            </button>
          );
        })}
      </nav>

      {/* Business info */}
      <div className="px-2 py-3 border-t border-orange-600">
        <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-white/10">
          <div className="w-7 h-7 rounded-full bg-white/30 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
            {orgInitials}
          </div>
          {isOpen && (
            <div className="min-w-0">
              <div className="text-white text-xs font-medium truncate">
                {org?.name || '...'}
              </div>
              <div className="text-orange-300 text-xs">
                {planLabel} · {planPrice}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}